import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Egg, 
  Calendar, 
  Save,
  Trash2,
  Package,
  RefreshCw,
  Edit2,
  X,
  Check,
  History,
  Clock,
  TrendingUp
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// EggInputCard component moved outside to prevent re-renders causing focus loss
const EggInputCard = ({ size, formData, handleInputChange, isEditing }) => (
  <div className={`rounded-xl border-2 ${size.color} p-4`}>
    <h4 className="font-semibold mb-3 flex items-center gap-2">
      <Egg className="w-4 h-4" />
      {size.label}
    </h4>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-xs opacity-70">Crates</Label>
        <Input
          type="number"
          min="0"
          value={formData[`${size.key}_crates`]}
          onChange={(e) => handleInputChange(`${size.key}_crates`, e.target.value)}
          className="mt-1"
          disabled={!isEditing}
          data-testid={`input-${size.key}-crates`}
        />
      </div>
      <div>
        <Label className="text-xs opacity-70">Pieces</Label>
        <Input
          type="number"
          min="0"
          value={formData[`${size.key}_pieces`]}
          onChange={(e) => handleInputChange(`${size.key}_pieces`, e.target.value)}
          className="mt-1"
          disabled={!isEditing}
          data-testid={`input-${size.key}-pieces`}
        />
      </div>
      {size.hasCrack && (
        <>
          <div>
            <Label className="text-xs text-orange-600">Crack Crates</Label>
            <Input
              type="number"
              min="0"
              value={formData[`${size.key}_crack_crates`]}
              onChange={(e) => handleInputChange(`${size.key}_crack_crates`, e.target.value)}
              className="mt-1 border-orange-200"
              disabled={!isEditing}
              data-testid={`input-${size.key}-crack-crates`}
            />
          </div>
          <div>
            <Label className="text-xs text-orange-600">Crack Pieces</Label>
            <Input
              type="number"
              min="0"
              value={formData[`${size.key}_crack_pieces`]}
              onChange={(e) => handleInputChange(`${size.key}_crack_pieces`, e.target.value)}
              className="mt-1 border-orange-200"
              disabled={!isEditing}
              data-testid={`input-${size.key}-crack-pieces`}
            />
          </div>
        </>
      )}
    </div>
  </div>
);

const PenFloorDailyEggProduction = () => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const isManager = user.role === 'manager';
  
  // Get assigned pens from user data - if empty, allow all pens
  const assignedPens = user.assigned_pens || [];
  const hasAssignedPens = assignedPens.length > 0;
  
  // Available pens: Admin/Manager see all, Staff see assigned (or all if none assigned)
  const availablePens = (isAdmin || isManager || !hasAssignedPens) 
    ? [1, 2, 3, 4, 5, 6, 7, 8] 
    : assignedPens.sort((a, b) => a - b);
  
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPen, setSelectedPen] = useState(availablePens[0]?.toString() || '1');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingEntry, setExistingEntry] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [allPenEntries, setAllPenEntries] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  
  // Mortality state
  const [mortalityData, setMortalityData] = useState({ count: '', cause: '', notes: '' });
  const [existingMortality, setExistingMortality] = useState(null);
  const [savingMortality, setSavingMortality] = useState(false);
  
  // Census data for % lay calculation
  const [censusData, setCensusData] = useState({});

  // % Lay trend across pens (current month)
  const [layTrend, setLayTrend] = useState({ pens: [], series: [], averages: {} });
  const [trendLoading, setTrendLoading] = useState(false);
  const [layTarget, setLayTarget] = useState(80);
  
  // Form data for selected pen
  const [formData, setFormData] = useState({
    extra_large_crates: '',
    extra_large_pieces: '',
    extra_large_crack_crates: '',
    extra_large_crack_pieces: '',
    large_crates: '',
    large_pieces: '',
    large_crack_crates: '',
    large_crack_pieces: '',
    medium_crates: '',
    medium_pieces: '',
    medium_crack_crates: '',
    medium_crack_pieces: '',
    small_crates: '',
    small_pieces: '',
    small_crack_crates: '',
    small_crack_pieces: '',
    damaged_crates: '',
    damaged_pieces: '',
    notes: ''
  });

  // Pens array: 1-6 regular pens, 7 is Brooding Pen, 8 is Grower Pen
  const pens = [1, 2, 3, 4, 5, 6, 7, 8];
  const getPenLabel = (pen) => {
    if (pen === 7) return 'Brooding Pen';
    if (pen === 8) return 'Grower Pen';
    return `Pen ${pen}`;
  };
  
  const eggSizes = [
    { key: 'extra_large', label: 'Extra Large', color: 'bg-purple-100 border-purple-300 text-purple-800', hasCrack: true },
    { key: 'large', label: 'Large', color: 'bg-blue-100 border-blue-300 text-blue-800', hasCrack: true },
    { key: 'medium', label: 'Medium', color: 'bg-green-100 border-green-300 text-green-800', hasCrack: true },
    { key: 'small', label: 'Small', color: 'bg-yellow-100 border-yellow-300 text-yellow-800', hasCrack: true },
    { key: 'damaged', label: 'Damaged', color: 'bg-red-100 border-red-300 text-red-800', hasCrack: false }
  ];

  // Mortality causes
  const mortalityCauses = [
    'Disease', 'Heat Stress', 'Cold Stress', 'Accident', 'Predator', 
    'Suffocation', 'Unknown', 'Other'
  ];

  useEffect(() => {
    fetchPenProduction();
    fetchAllPenEntries();
    fetchMortality();
    fetchCensusData();
  }, [selectedDate, selectedPen]);

  // Fetch % lay trend for the month of the selected date
  useEffect(() => {
    fetchLayTrend();
    // eslint-disable-next-line
  }, [selectedDate.slice(0, 7)]);

  const fetchLayTrend = async () => {
    setTrendLoading(true);
    try {
      const d = new Date(selectedDate + 'T00:00:00');
      const y = d.getFullYear();
      const m = d.getMonth();
      const pad = (n) => String(n).padStart(2, '0');
      const start = `${y}-${pad(m + 1)}-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      const end = `${y}-${pad(m + 1)}-${pad(lastDay)}`;
      const response = await fetch(
        `${API_URL}/api/pen-lay-trend?start=${start}&end=${end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setLayTrend(data);
      }
    } catch (error) {
      console.error('Error fetching lay trend:', error);
    } finally {
      setTrendLoading(false);
    }
  };

  const penColors = {
    1: '#8b5cf6', 2: '#22d3ee', 3: '#f59e0b', 4: '#34d399',
    5: '#f43f5e', 6: '#38bdf8', 7: '#e879f9', 8: '#fb7185'
  };

  // Fetch census data for % lay calculation
  const fetchCensusData = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/chicken-census/summary?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCensusData(data.pen_census || {});
      }
    } catch (error) {
      console.error('Error fetching census data:', error);
    }
  };

  // Calculate % lay for a pen
  const calculatePercentLay = (penEntry) => {
    if (!penEntry) return null;
    
    // Get ending census for this pen
    const penNum = penEntry.pen_number;
    const census = censusData[penNum];
    const endingCensus = census?.ending_census || 0;
    
    if (endingCensus === 0) return null;
    
    // Calculate total eggs (convert crates to pieces: 1 crate = 30 eggs)
    const crateToEggs = 30;
    const totalEggs = 
      ((penEntry.extra_large_crates || 0) * crateToEggs + (penEntry.extra_large_pieces || 0)) +
      ((penEntry.large_crates || 0) * crateToEggs + (penEntry.large_pieces || 0)) +
      ((penEntry.medium_crates || 0) * crateToEggs + (penEntry.medium_pieces || 0)) +
      ((penEntry.small_crates || 0) * crateToEggs + (penEntry.small_pieces || 0)) +
      // Include cracks
      ((penEntry.extra_large_crack_crates || 0) * crateToEggs + (penEntry.extra_large_crack_pieces || 0)) +
      ((penEntry.large_crack_crates || 0) * crateToEggs + (penEntry.large_crack_pieces || 0)) +
      ((penEntry.medium_crack_crates || 0) * crateToEggs + (penEntry.medium_crack_pieces || 0)) +
      ((penEntry.small_crack_crates || 0) * crateToEggs + (penEntry.small_crack_pieces || 0)) +
      // Damaged
      ((penEntry.damaged_crates || 0) * crateToEggs + (penEntry.damaged_pieces || 0));
    
    // % Lay = (Total eggs / Ending Census) * 100
    const percentLay = (totalEggs / endingCensus) * 100;
    return percentLay.toFixed(1);
  };

  const fetchMortality = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/pen-mortality?date=${selectedDate}&pen_number=${selectedPen}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setExistingMortality(data);
          setMortalityData({
            count: data.count?.toString() || '',
            cause: data.cause || '',
            notes: data.notes || ''
          });
        } else {
          setExistingMortality(null);
          setMortalityData({ count: '', cause: '', notes: '' });
        }
      }
    } catch (error) {
      console.error('Error fetching mortality:', error);
    }
  };

  const handleSaveMortality = async () => {
    if (!mortalityData.count || parseInt(mortalityData.count) <= 0) {
      toast.error('Please enter a valid mortality count');
      return;
    }

    setSavingMortality(true);
    try {
      const response = await fetch(`${API_URL}/api/pen-mortality`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          date: selectedDate,
          pen_number: parseInt(selectedPen),
          count: parseInt(mortalityData.count),
          cause: mortalityData.cause || null,
          notes: mortalityData.notes || null
        })
      });

      if (response.ok) {
        toast.success(`${getPenLabel(parseInt(selectedPen))} mortality recorded`);
        fetchMortality();
      } else {
        throw new Error('Failed to save mortality');
      }
    } catch (error) {
      toast.error('Failed to save mortality record');
    } finally {
      setSavingMortality(false);
    }
  };

  const handleDeleteMortality = async () => {
    if (!existingMortality) return;
    if (!window.confirm('Delete this mortality record?')) return;

    try {
      const response = await fetch(`${API_URL}/api/pen-mortality/${existingMortality.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Mortality record deleted');
        setExistingMortality(null);
        setMortalityData({ count: '', cause: '', notes: '' });
      }
    } catch (error) {
      toast.error('Failed to delete mortality record');
    }
  };

  const fetchPenProduction = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/daily-pen-production?date=${selectedDate}&pen_number=${selectedPen}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const entry = data[0];
          setExistingEntry(entry);
          setFormData({
            extra_large_crates: entry.extra_large_crates?.toString() || '',
            extra_large_pieces: entry.extra_large_pieces?.toString() || '',
            extra_large_crack_crates: entry.extra_large_crack_crates?.toString() || '',
            extra_large_crack_pieces: entry.extra_large_crack_pieces?.toString() || '',
            large_crates: entry.large_crates?.toString() || '',
            large_pieces: entry.large_pieces?.toString() || '',
            large_crack_crates: entry.large_crack_crates?.toString() || '',
            large_crack_pieces: entry.large_crack_pieces?.toString() || '',
            medium_crates: entry.medium_crates?.toString() || '',
            medium_pieces: entry.medium_pieces?.toString() || '',
            medium_crack_crates: entry.medium_crack_crates?.toString() || '',
            medium_crack_pieces: entry.medium_crack_pieces?.toString() || '',
            small_crates: entry.small_crates?.toString() || '',
            small_pieces: entry.small_pieces?.toString() || '',
            small_crack_crates: entry.small_crack_crates?.toString() || '',
            small_crack_pieces: entry.small_crack_pieces?.toString() || '',
            damaged_crates: entry.damaged_crates?.toString() || '',
            damaged_pieces: entry.damaged_pieces?.toString() || '',
            notes: entry.notes || ''
          });
          setIsEditing(false);
        } else {
          setExistingEntry(null);
          resetForm();
          setIsEditing(true);
        }
      }
    } catch (error) {
      console.error('Error fetching pen production:', error);
      toast.error('Failed to load pen production');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPenEntries = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/daily-pen-production?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAllPenEntries(data);
      }
    } catch (error) {
      console.error('Error fetching all pen entries:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      extra_large_crates: '',
      extra_large_pieces: '',
      extra_large_crack_crates: '',
      extra_large_crack_pieces: '',
      large_crates: '',
      large_pieces: '',
      large_crack_crates: '',
      large_crack_pieces: '',
      medium_crates: '',
      medium_pieces: '',
      medium_crack_crates: '',
      medium_crack_pieces: '',
      small_crates: '',
      small_pieces: '',
      small_crack_crates: '',
      small_crack_pieces: '',
      damaged_crates: '',
      damaged_pieces: '',
      notes: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        date: selectedDate,
        pen_number: parseInt(selectedPen),
        extra_large_crates: parseInt(formData.extra_large_crates) || 0,
        extra_large_pieces: parseInt(formData.extra_large_pieces) || 0,
        extra_large_crack_crates: parseInt(formData.extra_large_crack_crates) || 0,
        extra_large_crack_pieces: parseInt(formData.extra_large_crack_pieces) || 0,
        large_crates: parseInt(formData.large_crates) || 0,
        large_pieces: parseInt(formData.large_pieces) || 0,
        large_crack_crates: parseInt(formData.large_crack_crates) || 0,
        large_crack_pieces: parseInt(formData.large_crack_pieces) || 0,
        medium_crates: parseInt(formData.medium_crates) || 0,
        medium_pieces: parseInt(formData.medium_pieces) || 0,
        medium_crack_crates: parseInt(formData.medium_crack_crates) || 0,
        medium_crack_pieces: parseInt(formData.medium_crack_pieces) || 0,
        small_crates: parseInt(formData.small_crates) || 0,
        small_pieces: parseInt(formData.small_pieces) || 0,
        small_crack_crates: parseInt(formData.small_crack_crates) || 0,
        small_crack_pieces: parseInt(formData.small_crack_pieces) || 0,
        damaged_crates: parseInt(formData.damaged_crates) || 0,
        damaged_pieces: parseInt(formData.damaged_pieces) || 0,
        notes: formData.notes
      };

      const response = await fetch(`${API_URL}/api/daily-pen-production`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(`${getPenLabel(parseInt(selectedPen))} production saved successfully`);
        fetchPenProduction();
        fetchAllPenEntries();
        fetchLayTrend();
        setIsEditing(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save production');
      }
    } catch (error) {
      console.error('Error saving production:', error);
      toast.error('Failed to save production');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingEntry) return;
    
    if (!window.confirm('Are you sure you want to delete this production entry?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/daily-pen-production/${existingEntry.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Production entry deleted successfully');
        setExistingEntry(null);
        resetForm();
        setIsEditing(true);
        fetchAllPenEntries();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const handleCancelEdit = () => {
    if (existingEntry) {
      // Restore original values
      setFormData({
        extra_large_crates: existingEntry.extra_large_crates?.toString() || '',
        extra_large_pieces: existingEntry.extra_large_pieces?.toString() || '',
        extra_large_crack_crates: existingEntry.extra_large_crack_crates?.toString() || '',
        extra_large_crack_pieces: existingEntry.extra_large_crack_pieces?.toString() || '',
        large_crates: existingEntry.large_crates?.toString() || '',
        large_pieces: existingEntry.large_pieces?.toString() || '',
        large_crack_crates: existingEntry.large_crack_crates?.toString() || '',
        large_crack_pieces: existingEntry.large_crack_pieces?.toString() || '',
        medium_crates: existingEntry.medium_crates?.toString() || '',
        medium_pieces: existingEntry.medium_pieces?.toString() || '',
        medium_crack_crates: existingEntry.medium_crack_crates?.toString() || '',
        medium_crack_pieces: existingEntry.medium_crack_pieces?.toString() || '',
        small_crates: existingEntry.small_crates?.toString() || '',
        small_pieces: existingEntry.small_pieces?.toString() || '',
        small_crack_crates: existingEntry.small_crack_crates?.toString() || '',
        small_crack_pieces: existingEntry.small_crack_pieces?.toString() || '',
        damaged_crates: existingEntry.damaged_crates?.toString() || '',
        damaged_pieces: existingEntry.damaged_pieces?.toString() || '',
        notes: existingEntry.notes || ''
      });
    }
    setIsEditing(false);
  };

  const fetchEditHistory = async () => {
    if (!existingEntry?.id) return;
    
    try {
      const response = await fetch(`${API_URL}/api/daily-pen-production/${existingEntry.id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEditHistory(data.edit_history || []);
        setShowHistory(true);
      }
    } catch (error) {
      console.error('Error fetching edit history:', error);
      toast.error('Failed to load edit history');
    }
  };

  const formatFieldName = (field) => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Check if a pen has data for the selected date
  const penHasData = (penNumber) => {
    return allPenEntries.some(entry => entry.pen_number === penNumber);
  };

  return (
    <div className="space-y-6" data-testid="pen-floor-production-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Pen Floor Daily Egg Production & Mortalities
        </h1>
        <p className="text-lg text-slate-600 mt-2">Record daily egg production and mortalities for your assigned pen</p>
      </div>

      {/* Date & Pen Selection */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-500" />
            <Label className="text-sm font-medium">Date:</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-44"
              data-testid="date-picker"
            />
          </div>
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-slate-500" />
            <Label className="text-sm font-medium">Select Pen:</Label>
            <Select value={selectedPen} onValueChange={setSelectedPen}>
              <SelectTrigger className="w-40" data-testid="pen-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availablePens.map(pen => (
                  <SelectItem key={pen} value={pen.toString()}>
                    <span className="flex items-center gap-2">
                      {getPenLabel(pen)}
                      {penHasData(pen) && (
                        <span className="text-emerald-500 text-xs">✓ Recorded</span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={() => { fetchPenProduction(); fetchAllPenEntries(); }}
            disabled={loading}
            className="ml-auto"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Prominent % Lay for selected pen */}
      {(() => {
        const penNum = parseInt(selectedPen);
        const point = layTrend.points?.[selectedDate]?.[String(penNum)];
        // % Lay uses the day's Starting Census from the backend census chain, consistent with the trend chart.
        const lay = point && point.pct != null ? point.pct.toFixed(1) : null;
        const endingCensus = point ? (point.census || null) : null;
        const crateToEggs = 30;
        const totalEggs = point ? point.eggs : (existingEntry ? (
          ((existingEntry.extra_large_crates || 0) * crateToEggs + (existingEntry.extra_large_pieces || 0)) +
          ((existingEntry.large_crates || 0) * crateToEggs + (existingEntry.large_pieces || 0)) +
          ((existingEntry.medium_crates || 0) * crateToEggs + (existingEntry.medium_pieces || 0)) +
          ((existingEntry.small_crates || 0) * crateToEggs + (existingEntry.small_pieces || 0)) +
          ((existingEntry.extra_large_crack_crates || 0) * crateToEggs + (existingEntry.extra_large_crack_pieces || 0)) +
          ((existingEntry.large_crack_crates || 0) * crateToEggs + (existingEntry.large_crack_pieces || 0)) +
          ((existingEntry.medium_crack_crates || 0) * crateToEggs + (existingEntry.medium_crack_pieces || 0)) +
          ((existingEntry.small_crack_crates || 0) * crateToEggs + (existingEntry.small_crack_pieces || 0)) +
          ((existingEntry.damaged_crates || 0) * crateToEggs + (existingEntry.damaged_pieces || 0))
        ) : 0);
        const layVal = lay !== null ? parseFloat(lay) : null;
        const grad = layVal === null ? 'from-slate-400 to-slate-500'
          : layVal >= 80 ? 'from-emerald-500 to-green-600'
          : layVal >= 60 ? 'from-blue-500 to-indigo-600'
          : layVal >= 40 ? 'from-amber-500 to-orange-600'
          : 'from-red-500 to-rose-600';
        return (
          <div className={`rounded-2xl p-5 shadow-lg bg-gradient-to-br ${grad} text-white`} data-testid="selected-pen-lay-card">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <span className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Egg className="w-7 h-7" />
                </span>
                <div>
                  <p className="text-sm font-medium text-white/80">% Lay — {getPenLabel(penNum)}</p>
                  <div className="text-5xl font-extrabold leading-none mt-1" data-testid="selected-pen-lay-value">
                    {layVal !== null ? `${lay}%` : '—'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6 text-right">
                <div>
                  <p className="text-xs text-white/70">Total Eggs Laid</p>
                  <p className="text-2xl font-bold">{totalEggs.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-white/70">Starting Census</p>
                  <p className="text-2xl font-bold">{endingCensus != null ? endingCensus.toLocaleString() : '—'}</p>
                </div>
              </div>
            </div>
            {layVal === null && (
              <p className="text-xs text-white/80 mt-3">
                {!existingEntry ? 'No production recorded yet for this pen & date.' : 'No Starting Census recorded for this pen on this date — % lay cannot be calculated.'}
              </p>
            )}
          </div>
        );
      })()}

      {/* Status Banner */}
      {existingEntry && !isEditing && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Check className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-800">Production Recorded</p>
              <p className="text-sm text-emerald-600">
                Recorded by {existingEntry.created_by_name} on {new Date(existingEntry.created_at).toLocaleString()}
                {existingEntry.updated_at && (
                  <span className="ml-2 text-amber-600">
                    (Last updated: {new Date(existingEntry.updated_at).toLocaleString()})
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(isAdmin || isManager) && (
              <Button
                variant="outline"
                size="sm"
                onClick={fetchEditHistory}
                data-testid="history-button"
              >
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
            )}
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  data-testid="edit-button"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  className="text-red-600 hover:bg-red-50"
                  data-testid="delete-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Production Entry Form */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Egg className="w-5 h-5 text-emerald-500" />
            Pen {selectedPen} Production - {selectedDate}
          </h2>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                {existingEntry && (
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    data-testid="cancel-button"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-production-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Production'}
                </Button>
              </>
            ) : (
              <span className="text-sm text-slate-500 italic">View mode - Click Edit to modify</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {eggSizes.map(size => (
            <EggInputCard 
              key={size.key} 
              size={size} 
              formData={formData}
              handleInputChange={handleInputChange}
              isEditing={isEditing}
            />
          ))}
        </div>

        <div className="mt-4">
          <Label className="text-sm font-medium">Notes (Optional)</Label>
          <Input
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Any additional notes for this pen's production..."
            className="mt-1"
            disabled={!isEditing}
            data-testid="notes-input"
          />
        </div>
      </div>

      {/* Mortality Recording Section */}
      <div className="card border-2 border-red-200 bg-red-50/30">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2 text-red-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Mortality Recording - {getPenLabel(parseInt(selectedPen))}
          </h2>
          {existingMortality && (
            <span className="text-xs px-2 py-1 bg-red-100 text-red-600 rounded-full">
              Recorded: {existingMortality.count} deaths
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="text-sm font-medium text-red-700">Mortality Count *</Label>
            <Input
              type="number"
              min="0"
              value={mortalityData.count}
              onChange={(e) => setMortalityData({...mortalityData, count: e.target.value})}
              placeholder="Number of deaths"
              className="mt-1 border-red-200 focus:border-red-400"
              data-testid="mortality-count-input"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-red-700">Cause</Label>
            <Select 
              value={mortalityData.cause} 
              onValueChange={(value) => setMortalityData({...mortalityData, cause: value})}
            >
              <SelectTrigger className="mt-1 border-red-200" data-testid="mortality-cause-select">
                <SelectValue placeholder="Select cause..." />
              </SelectTrigger>
              <SelectContent>
                {mortalityCauses.map(cause => (
                  <SelectItem key={cause} value={cause}>{cause}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium text-red-700">Notes (Optional)</Label>
            <Input
              value={mortalityData.notes}
              onChange={(e) => setMortalityData({...mortalityData, notes: e.target.value})}
              placeholder="Additional details..."
              className="mt-1 border-red-200 focus:border-red-400"
              data-testid="mortality-notes-input"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleSaveMortality}
            disabled={savingMortality || !mortalityData.count}
            className="bg-red-600 hover:bg-red-700"
            data-testid="save-mortality-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {savingMortality ? 'Saving...' : existingMortality ? 'Update Mortality' : 'Record Mortality'}
          </Button>
          {existingMortality && (isAdmin || isManager) && (
            <Button
              variant="outline"
              onClick={handleDeleteMortality}
              className="text-red-600 hover:bg-red-50 border-red-200"
              data-testid="delete-mortality-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
          {!mortalityData.count && (
            <span className="text-sm text-slate-500 ml-2">
              Enter 0 if no mortality today
            </span>
          )}
        </div>
      </div>

      {/* Quick Overview - Which pens have data for selected date */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-blue-500" />
          Recording Status for {selectedDate}
          {hasAssignedPens && !isAdmin && !isManager && (
            <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full ml-2">
              Your Assigned Pens
            </span>
          )}
        </h3>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
          {pens.map(pen => {
            const penEntry = allPenEntries.find(entry => entry.pen_number === pen);
            const hasData = !!penEntry;
            const isAvailable = availablePens.includes(pen);
            const isSelected = selectedPen === pen.toString();
            // % Lay from backend point (day's Starting Census from the census chain), consistent everywhere.
            const gridPoint = layTrend.points?.[selectedDate]?.[String(pen)];
            const percentLay = gridPoint && gridPoint.pct != null ? gridPoint.pct.toFixed(1) : null;
            
            return (
              <div
                key={pen}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  !isAvailable 
                    ? 'bg-slate-100 border-slate-200 opacity-50 cursor-not-allowed'
                    : hasData 
                      ? 'bg-emerald-50 border-emerald-300 hover:border-emerald-400 cursor-pointer' 
                      : 'bg-slate-50 border-slate-200 hover:border-slate-300 cursor-pointer'
                } ${isSelected && isAvailable ? 'ring-2 ring-emerald-500' : ''}`}
                onClick={() => isAvailable && setSelectedPen(pen.toString())}
              >
                <div className="font-bold text-lg">{getPenLabel(pen)}</div>
                <div className={`text-xs mt-1 ${
                  !isAvailable 
                    ? 'text-slate-400'
                    : hasData ? 'text-emerald-600' : 'text-slate-400'
                }`}>
                  {!isAvailable 
                    ? 'Not assigned'
                    : hasData ? '✓ Recorded' : 'Not recorded'
                  }
                </div>
                {/* % Lay Display */}
                {hasData && percentLay !== null && (
                  <div className={`mt-2 text-sm font-semibold px-2 py-1 rounded ${
                    parseFloat(percentLay) >= 80 ? 'bg-emerald-100 text-emerald-700' :
                    parseFloat(percentLay) >= 60 ? 'bg-blue-100 text-blue-700' :
                    parseFloat(percentLay) >= 40 ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {percentLay}% Lay
                  </div>
                )}
                {hasData && percentLay === null && (
                  <div className="mt-2 text-xs text-slate-400">
                    No census
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Legend for % Lay colors */}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-600">
          <span className="font-medium">% Lay Scale:</span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></span> 80%+
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></span> 60-79%
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></span> 40-59%
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-red-100 border border-red-300"></span> &lt;40%
          </span>
        </div>
      </div>

      {/* % Lay Trend Comparison by Pen */}
      <div className="card" data-testid="lay-trend-panel">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <TrendingUp className="w-5 h-5" />
            </span>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">% Lay Trend by Pen</h3>
              <p className="text-xs text-slate-500">
                Daily percentage lay across pens — {new Date(selectedDate + 'T00:00:00').toLocaleString('default', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Target % Lay:</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={layTarget}
              onChange={(e) => setLayTarget(e.target.value === '' ? '' : Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
              className="w-20 h-8 text-sm"
              data-testid="lay-target-input"
            />
            <span className="text-sm text-slate-500">%</span>
          </div>
        </div>
        {trendLoading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading trend…
          </div>
        ) : (!layTrend.series || layTrend.series.length === 0) ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Egg className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">No % lay data for this month yet</p>
            <p className="text-xs">Record production and closing census to see the trend</p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={layTrend.series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => (d ? parseInt(d.split('-')[2], 10) : '')}
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  axisLine={false} tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  axisLine={false} tickLine={false} width={48}
                />
                <Tooltip
                  formatter={(value, name) => [`${value}%`, getPenLabel(parseInt(name))]}
                  labelFormatter={(d) => new Date(d + 'T00:00:00').toLocaleDateString()}
                  contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                  cursor={{ fill: 'rgba(148,163,184,0.1)' }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(name) => getPenLabel(parseInt(name))} />
                {layTarget !== '' && layTarget > 0 && (
                  <ReferenceLine
                    y={layTarget}
                    stroke="#059669"
                    strokeDasharray="6 4"
                    strokeWidth={2}
                    label={{ value: `Target ${layTarget}%`, position: 'insideTopLeft', fill: '#059669', fontSize: 11, fontWeight: 600 }}
                  />
                )}
                {(layTrend.pens || []).map((pen) => (
                  <Bar
                    key={pen}
                    dataKey={String(pen)}
                    name={String(pen)}
                    fill={penColors[pen] || '#64748b'}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={28}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            {/* Per-pen average chips */}
            <div className="mt-4 flex flex-wrap gap-2" data-testid="lay-trend-averages">
              {(layTrend.pens || []).map((pen) => {
                const avg = layTrend.averages?.[String(pen)] ?? 0;
                const below = layTarget !== '' && layTarget > 0 && avg < layTarget;
                return (
                  <span
                    key={pen}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${below ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}
                    data-testid={`lay-avg-pen-${pen}`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: penColors[pen] || '#64748b' }} />
                    <span className="text-slate-600">{getPenLabel(pen)} avg:</span>
                    <span className={`font-semibold ${below ? 'text-red-700' : 'text-emerald-700'}`}>{avg}%</span>
                    {below && <span className="text-[10px] font-semibold text-red-600 uppercase">Below target</span>}
                  </span>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Edit History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-500" />
              Edit History - Pen {selectedPen} ({selectedDate})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No edit history available</p>
                <p className="text-sm">This entry has not been modified since creation</p>
              </div>
            ) : (
              <div className="space-y-4">
                {editHistory.slice().reverse().map((entry, idx) => (
                  <div key={idx} className="border rounded-lg p-4 bg-slate-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{entry.edited_by_name}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(entry.edited_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        Edit #{editHistory.length - idx}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {Object.entries(entry.changes).map(([field, change]) => (
                        <div key={field} className="flex items-center gap-2 text-sm bg-white p-2 rounded border">
                          <span className="font-medium text-slate-600 min-w-[140px]">
                            {formatFieldName(field)}:
                          </span>
                          <span className="text-red-500 line-through">{change.old || '(empty)'}</span>
                          <span className="text-slate-400">→</span>
                          <span className="text-emerald-600 font-medium">{change.new || '(empty)'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PenFloorDailyEggProduction;
